"use client";

import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

const FAQSection: React.FC = () => {
  const t = useTranslations();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: t('faq_q1'),
      answer: t('faq_a1')
    },
    {
      question: t('faq_q2'),
      answer: t('faq_a2')
    },
    {
      question: t('faq_q3'),
      answer: t('faq_a3')
    },
    {
      question: t('faq_q4'),
      answer: t('faq_a4')
    },
    {
      question: t('faq_q6'),
      answer: t('faq_a6')
    },
    {
      question: t('faq_q7'),
      answer: t('faq_a7')
    },
    {
      question: t('faq_q8'),
      answer: t('faq_a8')
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-background relative overflow-hidden" id="faq">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>
      
      <div className="container-std relative z-10">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
            <HelpCircle size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              {t('faq_badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-foreground mb-6">
            {t('faq_title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('faq_desc')}
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="glass-card rounded-2xl border border-white/10 overflow-hidden"
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
              >
                <span className="font-bold text-foreground pr-4" itemProp="name">
                  {faq.question}
                </span>
                <ChevronDown 
                  size={20} 
                  className={`text-primary shrink-0 transition-transform duration-300 ${openIndex === idx ? 'rotate-180' : ''}`}
                />
              </button>
              
              <div 
                className={`overflow-hidden transition-[max-height] duration-300 ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}
              >
                <div className="p-6 pt-0 text-muted-foreground leading-relaxed" itemScope itemType="https://schema.org/Answer">
                  <div itemProp="text">{faq.answer}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional help */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            {t('faq_more')}
          </p>
          <Link 
            href="/contact" 
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors"
          >
            {t('faq_contact')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
